import React, { useState } from 'react';
import LoadingSpinner from '../../LoadingSpinner';
import { EVENT_TYPE } from '../../domain';
import { addEventWithDefaults } from '../../services/eventService';
import useUserStore from '../../stores/userStore';
import { AnimatePresence, motion } from 'framer-motion';
import { Send, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';

const NoteSubmitForm = () => {
    const { currentUser, selectedLocation, setRequestList, setEmployeeEvents } = useUserStore();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [noteText, setNoteText] = useState("");
    const [expanded, setExpanded] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!noteText.trim()) {
            alert("You can't enter an empty note.");
            return;
        }

        setIsSubmitting(true);

        try {
            await addEventWithDefaults(
                (newEvent) => {
                    setEmployeeEvents(currentUser.user_id, (prev) => [newEvent, ...prev]);
                    setRequestList((prev) => [newEvent, ...prev]);
                    setNoteText("");
                    setShowSuccess(true);
                    setTimeout(() => { setShowSuccess(false); setExpanded(false); }, 1500);
                },
                currentUser.user_id,
                currentUser.user_id,
                EVENT_TYPE.NOTE,
                selectedLocation,
                true,
                Date.now(),
                Date.now(),
                noteText
            );
        } catch (err) {
            console.error("Failed to submit note:", err);
            alert("There was an error submitting the note. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 overflow-hidden">
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="!w-full !flex !items-center !justify-between !px-5 !py-3 !bg-transparent !border-none !rounded-none hover:!bg-gray-50 !transition-colors"
            >
                <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Submit a Note</span>
                {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
            </button>

            <AnimatePresence>
                {expanded && (
                    <motion.form
                        onSubmit={handleSubmit}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="px-5 pb-4 flex gap-3 items-end">
                            <textarea
                                name="note"
                                placeholder="Type your note here..."
                                className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition resize-y min-h-[60px] max-h-[150px] text-sm"
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                                disabled={isSubmitting}
                            />
                            <div className="min-w-[80px] h-[42px] relative flex items-center justify-center">
                                <AnimatePresence mode="wait" initial={false}>
                                    {isSubmitting ? (
                                        <motion.div key="spin" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute"><LoadingSpinner /></motion.div>
                                    ) : showSuccess ? (
                                        <motion.div key="ok" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="absolute flex items-center gap-1 text-emerald-600 font-semibold text-sm">
                                            <CheckCircle size={16} /> Sent!
                                        </motion.div>
                                    ) : (
                                        <motion.button
                                            key="btn"
                                            type="submit"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="!absolute !inset-0 !flex !items-center !justify-center !gap-1.5 !bg-indigo-600 hover:!bg-indigo-700 !text-white !text-sm !font-medium !rounded-xl !border-none !shadow-sm hover:!shadow-md !transition-all"
                                        >
                                            <Send size={14} /> Send
                                        </motion.button>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    </motion.form>
                )}
            </AnimatePresence>
        </div>
    );
};

export default NoteSubmitForm;
